<template>
    <div id="medPlan" class="mt-1">
        <!-- add/edit dialog -->
        <v-dialog v-model="dialog" persistent width="500px" @keydown.esc="dialog = false">
            <v-form ref="form" @submit.prevent="submit">
                <v-card>
                    <v-card-title class="d-flex justify-end background-title-card mb-3 py-1">
                        <div class="flex-grow-1 text-center text-uppercase text-body-2 py-0 white--text">
                           {{ !visitId ? $t('MedicalRepPlan.add-new-visit') : $t('edit-visit') }}
                        </div>
                
                        <!-- close icon button -->
                        <v-btn icon @click="dialog = false">
                            <v-icon color="white" >
                                mdi-close-circle-outline
                            </v-icon>
                        </v-btn>
                    </v-card-title>
                
                    <v-card-text class="pb-0">
                        <v-row>
                            <!-- date -->
                            <v-col sm="12" md="12">
                                <bee-date-picker
                                    v-model="dateEvent"
                                    :input-label="$t('inputs.date')"
                                    hide-details="auto"
                                    menu-picker
                                    :input-props="{
                                        readonly: true,
                                        outlined: true,
                                        dense: true,
                                        disable: entityLoading
                                    }"
                                    :min-date="moment(selectedMonth).startOf('month').format('YYYY-MM-DD')"
                                    :max-date="moment(selectedMonth).endOf('month').format('YYYY-MM-DD')"
                                    :disabled="submitLoading" 
                                    :rules="rules.date"
                                    :allowed-dates="disableDates"
                                ></bee-date-picker>
                            </v-col>
                            <!-- type -->
                            <!--  -->
                            <v-col sm="12" md="6" class="pt-0">
                                <v-autocomplete
                                    v-model.number="type"
                                    :items="entityTypeArray"
                                    item-text="name"
                                    item-value="id"
                                    :label="$t('inputs.type')"
                                    :disabled="submitLoading || entityLoading"
                                    dense
                                    outlined
                                    hide-details
                                    @change="getEntityByType()" 
                                />
                                    <!-- -->
                            </v-col>
                            <!-- entity -->
                            <v-col sm="12" md="6" align-self="center" class="pt-0">
                                <v-autocomplete
                                    v-model="entityId"
                                    :items="type !== null ? entityByType : entityForMedicalRep"
                                    :label="$t('inputs.entity-name')"
                                    item-text="name"
                                    item-value="id"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    :disabled="submitLoading || entityLoading"
                                    :loading="entityLoading"
                                    :rules="rules.required"
                                >
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            text
                            type="submit"
                            color="success"
                            :loading="submitLoading"
                        >
                            {{ $t('save') }}
                        </v-btn>
                
                        <v-btn
                            text
                            color="alert-color"
                            @click="dialog = false"
                            :disabled="submitLoading"
                        >
                            {{ $t('cancel') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-form>
        </v-dialog>

        <!-- delete dialog -->
        <v-dialog v-model="deleteDialog" persistent width="400px" @keydown.esc="deleteDialog = false">
            <v-card>
                <v-card-title class="d-flex justify-end red-light-one-bg mb-3 py-1">
                    <div class="flex-grow-1 text-center text-uppercase text-body-2 py-0 white--text">
                        {{$t('delete')}}
                    </div>

                    <!-- close icon button -->
                    <v-btn icon @click="deleteDialog = false">
                        <v-icon color="white" >
                            mdi-close-circle-outline
                        </v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pb-0">
                    {{$t('are-you-sure-you-want-to')}} {{ $t('delete') }} {{$t('MedicalRepPlan.this-visit')}}?
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn
                    	text
                    	color="alert-color"
                    	:loading="submitLoading"
                    	@click="deleteEvent()"
                    >
                       {{$t('yes')}} 
                    </v-btn>

                    <v-btn
                    	text
                    	:disabled="submitLoading"
                    	@click="deleteDialog = false"
                    >
                        {{$t('no')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- filter -->
        <v-row align="center" class="mb-1">
            <!-- medical rep -->
            <v-col cols="12" sm="4" md="2">
                <v-autocomplete 
                    v-model="medicalRepId"
                    :label="$t('inputs.medical-rep')"
                    :items="medicalRepsBrief.filter(c => c.employeeType  === userType.medicalRep)"
                    item-value="id"
                    item-text="name"
                    outlined
                    dense
                    hide-details
                    :loading="loading"
                    :disabled="loading || Boolean($route.params.id)"
                    @change="fetchMedicalRepPlansVisits()"
                />
            </v-col>

            <!-- date input -->
            <v-col v-if="!weeklyPlan" align-self="center" cols="12" sm="4" md="3">
                <bee-date-input
					v-model="selectedMonth"
					:label="selectedMonth ? `${moment(selectedMonth).format('MMMM')}` : $t('inputs.date')"
					:loading="loading"
                    :disabled="loading || medicalRepId == null"
					dense
                    hide-details
                    input-format-order="year-month-day"
                    input-type="month"
					:append-icon="$i18n.locale === 'en' ? 'mdi-menu-right' : 'mdi-menu-left'"
					:prepend-icon="$i18n.locale === 'en' ? 'mdi-menu-left' : 'mdi-menu-right'"
                    readonly
                    fit-inputs
					@click:append="nextPlan(); matchVisitsWithPlan()"
					@click:prepend="prevPlan(); matchVisitsWithPlan()"
				></bee-date-input>
            </v-col>

            <v-col v-else class="d-flex align-center" cols="auto">
                <v-btn icon @click="prevPlan(); matchVisitsWithPlan()">
                    <v-icon> mdi-menu-right  </v-icon>
                </v-btn>
                <div>
                    <span> {{ startPlanDate ?  moment(startPlanDate).format('MMM D, yyyy') : $t('inputs.start-date') }} </span>
                    {{ '  ' + ' - ' + ' ' }}
                    <span> {{ endPlanDate ? moment(endPlanDate).format('MMM D, yyyy') : $t('inputs.end-date') }} </span>
                </div>
                <v-btn icon @click="nextPlan(); matchVisitsWithPlan()">
                    <v-icon> mdi-menu-left  </v-icon>
                </v-btn>
            </v-col>

            <v-col 
                align-self="center" 
                class="d-flex"
                :class="{
                    'justify-end': $vuetify.breakpoint.mdAndUp,
                    'justify-start pt-0':  $vuetify.breakpoint.smAndDown
                }"
            >
                <v-btn
                    v-if="excelFile"
                    small
                    color="green lighten-1 white--text"
                    @click="ImportExcel"
                    class="mx-1"
                    :loading="loadingExcel"
                    :disabled="loadingExcel || !medicalRepId || !medicalRepPlans.length"
                >
                    <v-icon> mdi-file-table-outline </v-icon>
                    {{$t('export-excel')}}
                </v-btn>
                <v-divider vertical class="mx-3" />

                <!-- BACK -->
                <v-btn
                    color="grey darken-2 white--text"
                    small
                    :to="{
                        name: 'staff',
                        query: {
                            role: $route.query.role
                        }
                    }"
                >
                    <v-icon small class="me-1"> mdi-arrow-left-circle </v-icon>
                    {{$t('back')}}
                </v-btn>
            </v-col>
        </v-row>

        <!-- calendar -->
        <v-card
            :max-height="$vuetify.breakpoint.height - 110"
            elevation="1"
            class="overflow-x-hidden"
        	:class="{'pt-3': $vuetify.breakpoint.smAndDown}"
        >
            <v-progress-linear v-if="loading" indeterminate height="2" class="progress-loader"/>
            <v-card-text class="pa-0">
                <v-calendar
                    ref="calendar"
                    v-model="calendarDate"
                    :events="events"
                    color="primary"
                    :type="weeklyPlan ? 'week' : 'month' "
                    :event-more="false"
                    @click:event="showMenu"
                    :now="moment().format('YYYY-MM-DD')"
                    :interval-count="0"
                    :show-interval-label="(() => { return false })" 
                    :weekdays="isDisabledFriday ? [0, 1, 2, 3, 4, 6] : [0, 1, 2, 3, 4, 5, 6]"
                >
                    <!-- -->
                    <!-- event card -->
                    <template v-slot:event="{ event }">
                        <p class="text-center"> 
                            <span v-if="$i18n.locale !== 'en'">
                                {{
                                    event.entityType === entityType.doctor
                                        ? medicalSystem ? '.Dr' : 'ز.ت'
                                        : event.entityType === entityType.pharmacy ? medicalSystem ? '.Ph' : 'ز.ج' : medicalSystem ? '.C' : 'م.'
                                }}
                                <!-- {{event.entityType}}
                                {{ entitiesBrief.find(c => c.id === event.entityId) ? 
                                    entitiesBrief.find(c => c.id === event.entityId).type === entityType.doctor ? medicalSystem ? '.Dr' : 'ز.ت'
                                    : entitiesBrief.find(c => c.id === event.entityId).type === entityType.pharmacy ? '.Ph' : 'ز.ج' : medicalSystem ? '.C' : 'م.'
                                }} -->
                            </span>
                            {{
                                entitiesBrief.find(c => c.id === event.entityId)
                                    ? entitiesBrief.find(c => c.id === event.entityId).name
                                    : event.name
                            }} 
                            <span v-if="$i18n.locale === 'en'">
                                {{
                                    event.entityType === entityType.doctor
                                        ? medicalSystem ? '.Dr' : 'ز.ت'
                                        : event.entityType === entityType.pharmacy ? medicalSystem ? '.Ph' : 'ز.ج' : medicalSystem ? '.C' : 'م.'
                                }}
                            </span>
                        </p>
                    </template>
                    <template v-if="weeklyPlan" v-slot:day-header="{ date }">
                        <v-btn
                            v-if="
                                (isSuperAdmin || permissionsUser === roleName.action) &&
                                moment(date).isBetween(moment().startOf('week'), moment().endOf('week') , null, [])
                            "
                            icon
                            color="success"
                            class="d-block mx-auto"
                            :disabled="
                                loading ||
                                !medicalRepPlans.length ||
                                !medicalRepId
                            "
                            @click="dateEvent = date; visited = null; dialog = true"
                        >
                            <!--            !Boolean(
                                    medicalRepPlans.find(c => moment(c.fromDate).isSame(selectedMonth, 'month'))
                                )
                                || moment(moment(date).format('YYYY-MM-DD')).isBefore(moment(), 'day') -->
                            <v-icon> mdi-plus-circle </v-icon>
                        </v-btn>
                    </template>
                    
                    <!-- add event button -->
                    <!-- monthly plan -->
                    <template v-slot:day="{ date }">
                        <v-btn
                            v-if="
                                (isSuperAdmin || permissionsUser === roleName.action) &&
                                moment(date).isSame(moment(), 'month')
                            "
                            icon
                            color="success"
                            class="d-block mx-auto"
                            :disabled="
                                loading ||
                                !medicalRepPlans.length ||
                                !medicalRepId ||
                                !Boolean(
                                    medicalRepPlans.find(c => moment(c.fromDate).isSame(selectedMonth, 'month'))
                                )
                                || moment(moment(date).format('YYYY-MM-DD')).isBefore(moment(), 'day')
                            "
                            @click="dateEvent = date; visited = null; dialog = true"
                        >
                            <v-icon> mdi-plus-circle </v-icon>
                        </v-btn>
                    </template> 
                </v-calendar>
            </v-card-text>
        </v-card>

        <!-- menu -->
        <v-menu
            v-model="menu"
            :activator="selectedElement"
            offset-x
        >
            <v-list dense>
                <!-- edit -->
                <v-list-item @click="dialog = true">
                    <v-list-item-icon class="me-5">
                        <v-icon color="edit-color">
                            mdi-pencil
                        </v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{$t('edit')}}</v-list-item-title>
                </v-list-item>

                <!-- delete -->
                <v-list-item @click="deleteDialog = true">
                    <v-list-item-icon class="me-5">
                        <v-icon color="alert-color">
                            mdi-delete
                        </v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{$t('delete')}}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';
import { roleName, userType, entityType, entityTypeArray } from '@/helpers/enums';
import rules from '@/validation rules';
import { excelFile, medicalSystem, weeklyPlan, activateNotification, isDisabledFriday } from '@/configs/global.config'
import { disableDates } from '@/helpers/functions'

export default {
    name: 'MedicalRepPlan',

    data: () => ({
        entityTypeArray,
        excelFile,
        // helpers
        entityType,
        roleName,
        userType,
        rules,
        
        submitLoading: false,
        loading: false,

        // add/edit dialog
        dialog: false,
        dateEvent: null,
        entityId: null,

        // delete dialog
        deleteDialog: false,
        visitId: null,

        // filters
        medicalRepId: null,
        selectedMonth: null,
        medicalRepPlans: [],

        // calendar
        selectedElement: null,
        menu: false,
        calendarDate: null,
        events: [],
        loadingExcel: false,
        type: entityType.doctor,
        entityLoading: false,
        entityForMedicalRep: [],
        entityByType: [],
		myData: [],

        medicalSystem,
        weeklyPlan,
        endPlanDate: null,
        startPlanDate: null,
        activateNotification,
        isDisabledFriday,
        disableDates
    }),

    watch: {
        dialog (val) {
            if (!val) {
                this.$refs.form.reset();
                this.entityId = null;
                this.visitId = null;
                this.type = null
            } else {
                this.entityByType = this.type !== null ? this.entityForMedicalRep.filter(c => c.type === this.type) : this.entityForMedicalRep
                return this.entityByType
            }
        }
    },
    computed: {
        ...mapState({
            plan: state => state.visitsPlan.plan,
            plannedVisits: state => state.visitsPlan.plan.plannedVisits,
            medicalRepsBrief: state => state.medicalreps.medicalRepsBrief,
            plannedVisitsByEmployeeId: state => state.visitsPlan.plannedVisitsByEmployeeId,
            permissionsUser: state => state.auth.youPermissions.MedicalPlans,
            isSuperAdmin: state => state.auth.isSuperAdmin,
            entitiesBrief: state => state.entities.entitiesBrief,
        }),

        ...mapGetters({
            getPlanById: 'visitsPlan/getPlanById',
            getVisitById: 'visitsPlan/getVisitById',
            getPlan: 'visitsPlan/getPlanByDate'
        })
    },
    methods: {
        // helpers
        moment,
        ImportExcel() {
            this.loadingExcel = true
            this.loading = true
            this.$store.dispatch('exporter/fetchPlanExport', { 
                fromDate: this.selectedMonth, 
                employeeId: this.medicalRepId,
            }).finally(() => {
                this.loadingExcel = false
                this.loading = false
            })
        },

        showMenu ({ nativeEvent, event }) {
            // console.log(event.entityType)
            // prevent adding out of 2 months
            // ![0, 1].includes(moment(this.selectedMonth).diff(moment(), 'months'))
            if (
                event.isOutOfPlan || moment(moment(event.date).format('YYYY-MM-DD')).isBefore(moment(), 'day')
                
            ) return;
            // console.log(event)
            if (event.isOutOfPlan) return;
            this.visitId = event.id;
            this.dateEvent = moment(event.start).format('YYYY-MM-DD');
            this.entityId = event.entityId;
            this.type = event.entityType
            this.selectedElement = nativeEvent.target;
            this.$nextTick(() => { this.menu = true; })
        },

        nextPlan () {
            if (!this.weeklyPlan) {
                this.selectedMonth = moment(this.selectedMonth).add(1, 'month').format('YYYY-MM')
            } else {
                this.startPlanDate = moment(this.endPlanDate).add(1, 'day').format('MMM D, YYYY')
                this.endPlanDate = moment(this.endPlanDate).add(7, 'day').format('MMM D, YYYY')
            }
            this.$refs.calendar.next();
		},
		prevPlan () {
            if (!this.weeklyPlan) { 
                this.selectedMonth = moment(this.selectedMonth).subtract(1, 'month').format('YYYY-MM')
                this.$refs.calendar.prev();
            } else {
                this.$refs.calendar.prev();
                this.startPlanDate = this.calendarDate
                this.endPlanDate = moment(this.calendarDate).add(6, 'day').format('MMM D, YYYY')
            }
		},

        // manage planned visits
        submit() {
            if (this.$refs.form.validate()) {
                this.submitLoading = true;
                const actionName = !this.visitId ? 'create' : 'update';
                this.$store.dispatch(`visitsPlan/${actionName}`, { 
                    id:  this.visitId,
                    entityId: this.entityId, 
                    date: `${this.dateEvent}T00:00:00`,
                    visitPlanId: this.plan.map(c => ({
                        ...c,
                        fromDate: moment(c.fromDate).format('YYYY-MM')
                    })).find(c => moment(c.fromDate).isSame(this.selectedMonth, 'month'))?.id,
                    notify: this.visitId !== null && this.activateNotification
                }).then(() => {
                    this.matchVisitsWithPlan();
                    this.dialog = false;
                }).finally(() => {
                    this.submitLoading = false
                })
            }
        },

        deleteEvent () {
            this.submitLoading = true;
            this.$store.dispatch('visitsPlan/delete', {
                id: this.visitId
            }).then(() => {
                this.matchVisitsWithPlan();
                this.deleteDialog = false;
            }).finally(() => {
                this.submitLoading = false;
            })
        },

        // plan visits
        fetchMedicalRepPlansVisits() {
            this.$store.dispatch('regions/fetchAllRegions', { employeeId: this.medicalRepId }).then((data) => {           
				this.$store.dispatch('entities/fetchAllBrief', {
					regionIds: data.length ? data.map(c => c.id) : [],
					preventStoreEntities: true
				}).then((data) => {
					this.entityForMedicalRep = data
				})
            })

            this.medicalRepPlans = [];
            this.events = [];
            if (!this.medicalRepId) {
                this.selectedMonth = null;
                return;
            };
            
            this.loading = true;
            this.$store.dispatch('visitsPlan/allPlan', { employeeId: this.medicalRepId }).then((data) => {
                this.medicalRepPlans = data;
                if (!data.length && !this.weeklyPlan) this.selectedMonth = this.moment().format('YYYY-MM-DD')
                // FIXME latest plan must be for latest plan only not for largest month
                // this is just solution for the unordered plans date
                const planForTheCurrentMonth = data.length ? data.find(c => moment(c.fromDate).isSame(moment(), 'month')) ? data.find(c => moment(c.fromDate).isSame(moment(), 'month')) : data[data.length - 1] : {};
                console.log(planForTheCurrentMonth)
                const planForTheCurrentWeek = data ? data.find(c => moment(c.fromDate).isSame(moment().startOf('week').format('YYYY-MM-DD'), 'day')) : {}
                // console.log(moment(Math.max(...data.map(c => new Date(c.fromDate)))).format('YYYY-MM'))
                if ((!planForTheCurrentMonth && !this.weeklyPlan) || (!planForTheCurrentWeek && this.weeklyPlan)) {
                    this.events = []
                    this.$eventBus.$emit(
                        'show-snackbar',
                        this.$t('MedicalRepPlan.message'),
                        'info'
                    );
                    this.loading = false;
                    return;
                }
                const planThisMonth = moment(planForTheCurrentMonth.fromDate).format('YYYY-MM');
                const planThisWeek = this.weeklyPlan === true ? moment(planForTheCurrentWeek.fromDate).format('YYYY-MM-DD') : null
                this.$refs.calendar.move(Math.abs(moment(this.selectedMonth).diff(planThisMonth, 'months')));
                
                this.selectedMonth = planThisMonth;
                this.startPlanDate = planThisWeek
                this.endPlanDate = this.weeklyPlan ? moment(planForTheCurrentWeek.toDate).format('YYYY-MM-DD') : null
                this.calendarDate = !this.weeklyPlan ? moment(planThisMonth).format('YYYY-MM-DD') : planThisWeek;

                this.matchVisitsWithPlan();
            })
        },

        matchVisitsWithPlan () {
            this.events = [];
            if (
                this.medicalRepPlans.length &&
                !this.medicalRepPlans.find(c => moment(c.fromDate).isSame(this.selectedMonth, 'month'))
            ) {
                this.$eventBus.$emit(
                    'show-snackbar',
                    this.$t('MedicalRepPlan.there-is-no-plan-created-for-the-selected-month'),
                    'info'
                );
                return 
            }

            this.loading = true;
            return Promise.all([
                this.$store.dispatch('visitsPlan/fetchPlannedVisitsByEmployeeId', { 
                    employeeId: this.medicalRepId,
                    fromDate: !this.weeklyPlan ? moment(this.selectedMonth).format('YYYY-MM-DD') : this.startPlanDate,
                    active: true
                }),
                this.$store.dispatch('visits/fetchVisitsWithoutPagination', { 
                    fromDate: !this.weeklyPlan ? moment(this.selectedMonth).startOf('month').format('YYYY-MM-DD') : this.startPlanDate,
                    toDate: !this.weeklyPlan ? moment(this.selectedMonth).endOf('month').format('YYYY-MM-DD') : this.endPlanDate,
                    employeeId: this.medicalRepId
                })
            ]).then(data => {
                const plannedVisits = data[0];
                let visits = data[1];

                for (let i = 0; i < (plannedVisits || []).length; i++) {
                    this.events.push({
                        name: plannedVisits[i].entity.name,
                        entityId: plannedVisits[i].entity.entityId,
                        id: plannedVisits[i].id,
                        color:
                            plannedVisits[i].isVisited
                                ? 'success'
                                : moment(plannedVisits[i].date).diff(moment(), 'days') >= 0
                                    ? 'grey' : 'red',
                        date: plannedVisits[i].date,
                        start: new Date(plannedVisits[i].date).getTime(),
                        end: new Date(plannedVisits[i].date).getTime(),
                        timed: false,
                        entityType: plannedVisits[i].entity.entityType
                    })
                }

                visits = (visits || []).map(c => ({
                    entity: c.entity,
                    entityId: c.entityId,
                    date: new Date(c.createdAt).getTime(),
                    entityType: c.entity.entityType
                }))

                for (let i = 0; i < visits.length; i++) {
                    const match = this.events.filter(c => c.entityId === visits[i].entityId)
                    if (!match.find(c => moment(c.date).isSame(visits[i].date, 'day'))) {
                        this.events.push({
                            name: visits[i].entity.name,
                            entityId: visits[i].entityId,   
                            start: visits[i].date,
                            end: visits[i].date,
                            isOutOfPlan: true,
                            timed: false,
                            entityType: visits[i].entityType,
                            color: 'orange'
                        })
                    }
                }
            }).finally(() => {
                this.loading = false;
            })
        },

        getEntityByType() {
            if (this.type !== null) {
                this.entityByType = this.entityForMedicalRep.filter(c => c.type === this.type)
                return this.entityByType
            } else {
                return this.entityForMedicalRep
            }
        }
    },

    created () {
        this.medicalRepId = this.$route.params.id || null;

        this.loading = true
        // Promise.all([
        this.$store.dispatch('entities/fetchAllBrief', { regionIds: [] })
        if (!this.medicalRepsBrief.length) {
            this.$store.dispatch('medicalreps/fetchAllBrief', true)
        }
        // ]).finally(() => {
        if (this.medicalRepId) {
            this.fetchMedicalRepPlansVisits()
        } else {
            this.loading = false
        }
        // })
    },

    metaInfo() {
        return {
            title: this.$t(`routes.${this.$route.meta.i18nKey}`),
            titleTemplate: '%s | Octophamra'
        }
    },
}
</script>

<style lang="scss">
#medPlan {
    .v-calendar-weekly__head {
        position: sticky;
        top: 0;
        background-color: white;
        z-index: 2;
    }
    .progress-loader {
        position: absolute;
        top:0;
        z-index: 3;
    }
}
</style>